import { inject } from '@angular/core';
import { EMPTY, forkJoin, from, pipe } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';

import { SaypStatementPdf } from '@save-as-you-pay/models/sayp-statement-pdf.model';
import { SaypStatementPdfsService } from '@save-as-you-pay/services/sayp-statement-pdfs.service';
import { StatementPdfDownloadService } from '@save-as-you-pay/services/statement-pdf-download.service';
import { SaypStatementsStore } from '@save-as-you-pay/store/sayp-statements.store';

const ui = {
  isLoading: false,
  error: null as string | null,
  isCollectionLoaded: false,
  isDownloading: false,
  downloadError: null as string | null,
};

export type PdfWithPath = SaypStatementPdf & { storagePath: string };

export const SaypStatementPdfsStore = signalStore(
  { providedIn: 'root' },
  withState(ui),
  withEntities<SaypStatementPdf>(),
  withMethods(
    (
      store,
      svc = inject(SaypStatementPdfsService),
      downloadSvc = inject(StatementPdfDownloadService),
      statementsStore = inject(SaypStatementsStore)
    ) => {
      const streamAllPdfs = rxMethod<void>(
        pipe(
          switchMap(() => {
            if (store.isCollectionLoaded()) {
              return EMPTY;
            }
            return svc.streamAllPdfs().pipe(
              tap({
                next: pdfs =>
                  patchState(store, setAllEntities(pdfs), {
                    isCollectionLoaded: true,
                    isLoading: false,
                    error: null,
                  }),
                error: () => {},
              }),
              catchError(err => {
                patchState(store, {
                  isLoading: false,
                  error: err.message ?? 'Failed to stream PDFs',
                });
                return EMPTY;
              })
            );
          })
        )
      );

      return {
        streamAllPdfs,
        loadPdfsData(): void {
          streamAllPdfs();
        },

        downloadPdfsSeparately: rxMethod(
          pipe(
            switchMap((pdfs: PdfWithPath[]) => {
              patchState(store, {
                isDownloading: true,
                downloadError: null,
              });
              const statementId = pdfs.length > 0 ? pdfs[0].statementId : null;
              const pdfsToMark = pdfs.filter(p => !p.didDownloadStatement);
              return from(downloadSvc.downloadPdfsSeparately(pdfs)).pipe(
                concatMap(() => {
                  if (pdfsToMark.length === 0) {
                    return from(Promise.resolve());
                  }
                  return forkJoin(
                    pdfsToMark.map(pdf =>
                      from(svc.markPdfDownloaded(pdf.id)).pipe(
                        catchError(() => EMPTY)
                      )
                    )
                  );
                }),
                tap({
                  next: () => {
                    pdfsToMark.forEach(p =>
                      patchState(
                        store,
                        updateEntity({
                          id: p.id,
                          changes: { didDownloadStatement: true },
                        })
                      )
                    );
                    patchState(store, {
                      isDownloading: false,
                      downloadError: null,
                    });
                    if (statementId) {
                      statementsStore.markStatementDownloaded(statementId);
                    }
                  },
                  error: () => {},
                }),
                catchError(err => {
                  patchState(store, {
                    isDownloading: false,
                    downloadError: err.message ?? 'Failed to download PDFs',
                  });
                  return EMPTY;
                })
              );
            })
          )
        ),

        downloadPdfsCombined: rxMethod(
          pipe(
            switchMap(
              ({
                pdfs,
                filename,
              }: {
                pdfs: PdfWithPath[];
                filename: string;
              }) => {
                patchState(store, {
                  isDownloading: true,
                  downloadError: null,
                });
                const statementId =
                  pdfs.length > 0 ? pdfs[0].statementId : null;
                const pdfsToMark = pdfs.filter(p => !p.didDownloadStatement);
                return from(
                  downloadSvc.downloadPdfsCombined(pdfs, filename)
                ).pipe(
                  concatMap(() => {
                    if (pdfsToMark.length === 0) {
                      return from(Promise.resolve());
                    }
                    return forkJoin(
                      pdfsToMark.map(pdf =>
                        from(svc.markPdfDownloaded(pdf.id)).pipe(
                          catchError(() => EMPTY)
                        )
                      )
                    );
                  }),
                  tap({
                    next: () => {
                      pdfsToMark.forEach(p =>
                        patchState(
                          store,
                          updateEntity({
                            id: p.id,
                            changes: { didDownloadStatement: true },
                          })
                        )
                      );
                      patchState(store, {
                        isDownloading: false,
                        downloadError: null,
                      });
                      if (statementId) {
                        statementsStore.markStatementDownloaded(statementId);
                      }
                    },
                    error: () => {},
                  }),
                  catchError(err => {
                    patchState(store, {
                      isDownloading: false,
                      downloadError: err.message ?? 'Failed to download PDFs',
                    });
                    return EMPTY;
                  })
                );
              }
            )
          )
        ),

        markPdfDownloaded: rxMethod(
          pipe(
            switchMap((pdfId: string) => {
              const pdf = store.entityMap()[pdfId];
              if (pdf?.didDownloadStatement) {
                return EMPTY;
              }
              return from(svc.markPdfDownloaded(pdfId)).pipe(
                tap({
                  next: () =>
                    patchState(
                      store,
                      updateEntity({
                        id: pdfId,
                        changes: { didDownloadStatement: true },
                      })
                    ),
                  error: () => {},
                }),
                catchError(() => EMPTY)
              );
            })
          )
        ),

        requestEmailSend: rxMethod(
          pipe(
            switchMap((pdfIds: string[]) => {
              const toRequest = pdfIds.filter(id => {
                const p = store.entityMap()[id];
                return p && !p.emailSendRequested;
              });
              if (toRequest.length === 0) {
                return EMPTY;
              }
              return from(svc.requestEmailSend(toRequest)).pipe(
                tap({
                  next: () => {
                    toRequest.forEach(id =>
                      patchState(
                        store,
                        updateEntity({
                          id,
                          changes: { emailSendRequested: true },
                        })
                      )
                    );
                  },
                  error: () => {},
                }),
                catchError(() => EMPTY)
              );
            })
          )
        ),
      };
    }
  )
);
